import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  AutocompleteRenderInputParams,
} from '@mui/material';
import { theme } from 'theme';
import { jbColors } from 'theme/jbColors';
import ContextIcon from 'assets/icons/Context.svg?react';
import { setIsRequiredNewBr, setPlaceHolderName } from 'reducers/mandateReducer';
import { useDispatch } from 'react-redux';
import { formatEnum } from 'utils/stringUtils';
import { NewAccountEnum } from 'types';
import CustomCircularProgress from 'components/common/CustomCircularProgress';

const styles = {
  container: {
    paddingLeft: theme.spacing(2),
  },
  icon: {
    marginTop: theme.spacing(2),
  },
  cardHeader: {
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  card: {
    maxWidth: '90%',
  },
  textInput: {
    marginTop: theme.spacing(0),
    '& .MuiFilledInput-inputMarginDense': {
      paddingLeft: theme.spacing(1.5),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  },
  labelRoot: {
    marginLeft: 0,
    marginRight: 0,
    width: '100%',
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(0),
  },
  labelPlacementTop: {
    alignItems: 'flex-start',
  },
  autocomplete: {
    padding: theme.spacing(0),
    margin: theme.spacing(0),
    border: 0,
    backgroundColor: jbColors.support.greenSmoke[10],
  },
  warning: {
    color: jbColors.technical.carminRed[100],
    paddingLeft: theme.spacing(2),
    paddingTop: theme.spacing(0.5),
  },
};

type Props = {
  id?: string;
  dropDownOptions: string[];
  isLoading: boolean;
};
export const DefineContext = ({ isLoading, dropDownOptions = [] }: Props) => {
  const dispatch = useDispatch();
  const [showWarning, setShowWarning] = useState(false);

  const handleChange = (onChangeValue: string) => {
    dispatch(setPlaceHolderName(onChangeValue));
  };

  return (
    <Card data-testid="define-context" sx={styles.card} square>
      <Grid container sx={styles.container}>
        <ContextIcon style={styles.icon} />
        <CardHeader title={'DEFINE CONTEXT'} titleTypographyProps={{ variant: 'subtitle1' }} sx={styles.cardHeader} />
      </Grid>
      <CardContent>
        {isLoading ? (
          <CustomCircularProgress size={30} />
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} lg={5}>
              <FormControlLabel
                // TODO - check this in browser, add it to default theme
                // classes={{
                //   root: classes.labelRoot,
                //   labelPlacementTop: classes.labelPlacementTop,
                // }}
                control={
                  dropDownOptions.length === 1 ? (
                    <TextField
                      fullWidth
                      defaultValue={dropDownOptions[0]}
                      id="define-context-account-text-input"
                      data-testid={'define-context-account-text-input'}
                      variant="filled"
                      margin="dense"
                      sx={styles.textInput}
                      InputProps={{ disableUnderline: true }}
                      disabled
                    />
                  ) : (
                    <Autocomplete
                      style={{ width: 'inherit' }}
                      id="define-context-account-drop-down-input"
                      options={dropDownOptions}
                      size="small"
                      onChange={(_: React.SyntheticEvent, value: string | null | undefined) => {
                        if (value) {
                          dispatch(setIsRequiredNewBr(value === formatEnum(NewAccountEnum.NEW)));
                          setShowWarning(false);
                        } else {
                          dispatch(setIsRequiredNewBr(undefined));
                          setShowWarning(true);
                        }
                      }}
                      defaultValue={dropDownOptions[0]}
                      getOptionLabel={(option) => (option ? option : '')}
                      renderInput={(params: AutocompleteRenderInputParams) => {
                        return (
                          <>
                            <TextField
                              onError={() => setShowWarning(!showWarning)}
                              {...params}
                              variant="filled"
                              sx={styles.autocomplete}
                              margin="dense"
                              inputProps={{
                                ...params.inputProps,
                                'data-testid': 'define-context-account-drop-down-input',
                              }}
                              InputProps={{
                                ...params.InputProps,
                                disableUnderline: true,
                              }}
                            />
                            <Typography
                              id={'define-context-account-drop-down-input-helper-text'}
                              data-testid={'define-context-account-drop-down-input-helper-text'}
                              hidden={!showWarning}
                              variant="body2"
                              color="primary"
                              sx={styles.warning}
                            >
                              At least one BR must be selected
                            </Typography>
                          </>
                        );
                      }}
                    />
                  )
                }
                label={'Account'}
                labelPlacement="top"
              />
            </Grid>
            <Grid item xs={12} lg={5}>
              <FormControlLabel
                control={
                  <TextField
                    id="define-context-account-placeholder-text-input"
                    fullWidth
                    variant="filled"
                    placeholder="Enter a name"
                    margin="dense"
                    sx={styles.textInput}
                    InputProps={{ disableUnderline: true }}
                    inputProps={{
                      'data-testid': 'define-context-account-placeholder-text-input',
                    }}
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
                      handleChange(event.target.value)
                    }
                  />
                }
                label={'Placeholder name'}
                labelPlacement="top"
              />
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};
